'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useScrollReveal } from '@/lib/hooks/use-scroll-reveal';
import { Footer } from '@/components/public/footer';
import { NavPreview } from '@/components/public/nav-preview';
import { ArrowRight, ChevronLeft, ChevronRight, ChevronDown, Users, Maximize, Monitor, Coffee, X, Check, Loader2 } from 'lucide-react';
import { LuBedDouble, LuBedSingle } from 'react-icons/lu';
import Image from 'next/image';
import { useTranslations, useLocale } from 'next-intl';
import { FaqAccordion } from '@/components/public/faq-accordion';
import { FAQ_CATEGORIES } from '@/lib/faq-content';
import { useFaqItems } from '@/lib/faq-hooks';
import { localeDateTag } from '@/lib/i18n-utils';
import { log } from '@/lib/logger';

// ─── Components ──────────────────────────────────────────────────────────────

function TestimonialsCarousel() {
  const t = useTranslations('design.corporate');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');

  const corporateTestimonials = [
    { name: t('testimonials.t1.name'), company: t('testimonials.t1.company'), text: t('testimonials.t1.text') },
    { name: t('testimonials.t2.name'), company: t('testimonials.t2.company'), text: t('testimonials.t2.text') },
    { name: t('testimonials.t3.name'), company: t('testimonials.t3.company'), text: t('testimonials.t3.text') },
    { name: t('testimonials.t4.name'), company: t('testimonials.t4.company'), text: t('testimonials.t4.text') },
  ];

  const goToNext = useCallback(() => {
    if (isAnimating) return;
    setDirection('next');
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % corporateTestimonials.length);
      setTimeout(() => setIsAnimating(false), 50);
    }, 400);
  }, [isAnimating, corporateTestimonials.length]);

  const goToPrev = () => {
    if (isAnimating) return;
    setDirection('prev');
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + corporateTestimonials.length) % corporateTestimonials.length);
      setTimeout(() => setIsAnimating(false), 50);
    }, 400);
  };

  const goToSlide = (index: number) => {
    if (isAnimating || index === currentIndex) return;
    setDirection(index > currentIndex ? 'next' : 'prev');
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex(index);
      setTimeout(() => setIsAnimating(false), 50);
    }, 400);
  };

  useEffect(() => {
    const timer = setInterval(goToNext, 7000);
    return () => clearInterval(timer);
  }, [goToNext]);

  const testimonial = corporateTestimonials[currentIndex];

  return (
    <div className="relative max-w-5xl mx-auto">
      <div className="bg-white rounded-3xl shadow-2xl px-12 md:px-20 lg:px-28 py-10 md:py-16 mx-4 relative overflow-hidden">
        {/* Decorative quotes */}
        <div className="absolute top-6 left-8 text-[#801515]/10 text-8xl font-serif leading-none">&ldquo;</div>
        <div className="absolute bottom-6 right-8 text-[#801515]/10 text-8xl font-serif leading-none">&rdquo;</div>

        <div
          key={currentIndex}
          className={`transition-all duration-500 ease-out ${
            isAnimating
              ? direction === 'next'
                ? 'opacity-0 -translate-x-12'
                : 'opacity-0 translate-x-12'
              : 'opacity-100 translate-x-0'
          }`}
        >
          <blockquote className="text-xl md:text-2xl text-gray-700 leading-relaxed mb-10 text-center font-light italic">
            &ldquo;{testimonial.text}&rdquo;
          </blockquote>

          <div className="text-center">
            <p className="font-semibold text-gray-900 text-xl">{testimonial.name}</p>
            <p className="text-[#801515] font-medium text-lg">{testimonial.company}</p>
          </div>
        </div>

        {/* Navigation arrows */}
        <button
          onClick={goToPrev}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white border-2 border-[#801515] shadow-lg hover:shadow-xl flex items-center justify-center text-[#801515] hover:bg-[#801515] hover:text-white transition-all duration-300 transform hover:scale-110"
          aria-label={t('testimonials.prev')}
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={goToNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white border-2 border-[#801515] shadow-lg hover:shadow-xl flex items-center justify-center text-[#801515] hover:bg-[#801515] hover:text-white transition-all duration-300 transform hover:scale-110"
          aria-label={t('testimonials.next')}
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-3 mt-8">
        {corporateTestimonials.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`transition-all duration-300 rounded-full ${
              index === currentIndex
                ? 'w-12 h-3 bg-[#801515]'
                : 'w-3 h-3 bg-gray-300 hover:bg-[#801515]/50'
            }`}
            aria-label={t('testimonials.goTo', { number: index + 1 })}
          />
        ))}
      </div>
    </div>
  );
}

function VenueCard({ venue, index }: { venue: { name: string; size: string; tagline: string; description: string; tech: string[]; coffeeBreak: string; seating: { type: string; capacity: number; icon: string }[]; image: string }; index: number }) {
  const [isOpen, setIsOpen] = useState(false);
  const t = useTranslations('design.corporate');

  return (
    <div
      className="observe-me slide-up bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500"
      style={{ animationDelay: `${index * 0.15}s` }}
    >
      {/* Image */}
      <div className="relative h-64 overflow-hidden">
        <Image
          src={venue.image}
          alt={venue.name}
          fill
          className="object-cover hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute bottom-4 left-6 right-6">
          <h3 className="text-2xl font-bold text-white font-poppins">{venue.name}</h3>
          <p className="text-white/80 text-sm italic">{venue.tagline}</p>
        </div>
        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full">
          <span className="text-[#801515] font-bold text-sm">{venue.size}</span>
        </div>
      </div>

      {/* Description */}
      <div className="p-6">
        <p className="text-gray-600 leading-relaxed mb-4">{venue.description}</p>

        {/* Quick stats */}
        <div className="flex flex-wrap gap-3 mb-4">
          {venue.seating.map((s) => (
            <div key={s.type} className="flex items-center gap-2 bg-[#faf8f6] px-4 py-2 rounded-full">
              <Users className="w-4 h-4 text-[#801515]" />
              <span className="text-sm font-medium text-gray-700">{s.type}: <strong className="text-[#801515]">{s.capacity}</strong></span>
            </div>
          ))}
        </div>

        {/* Expandable details */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 text-[#801515] font-medium hover:text-[#601010] transition-colors duration-300"
        >
          <span>{isOpen ? t('venues.lessDetails') : t('venues.fullSpecs')}</span>
          <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        <div className={`transition-all duration-500 ease-in-out overflow-hidden ${isOpen ? 'max-h-[400px] opacity-100 mt-4' : 'max-h-0 opacity-0'}`}>
          <div className="border-t border-gray-100 pt-4 space-y-3">
            <div className="flex items-start gap-3">
              <Monitor className="w-5 h-5 text-[#801515] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-gray-800 mb-1">{t('venues.techSetup')}</p>
                <p className="text-sm text-gray-600">{venue.tech.join(' \u00b7 ')}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Coffee className="w-5 h-5 text-[#801515] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-gray-800 mb-1">{t('venues.coffeeBreakLabel')}</p>
                <p className="text-sm text-gray-600">{venue.coffeeBreak}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Maximize className="w-5 h-5 text-[#801515] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-gray-800 mb-1">{t('venues.seatingOptions')}</p>
                <div className="flex flex-wrap gap-2">
                  {venue.seating.map((s) => (
                    <span key={s.type} className="bg-[#801515]/10 text-[#801515] px-3 py-1 rounded-full text-sm font-medium">
                      {s.type} &mdash; {s.capacity} {t('venues.pax')}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FAQAccordion() {
  const category = FAQ_CATEGORIES.find((c) => c.id === 'corporate')!;
  const items = useFaqItems(category);
  return <FaqAccordion items={items} accent={category.accent} />;
}

// ─── Twin Bed Icon ───────────────────────────────────────────────────────────

function TwinBedIcon() {
  return (
    <span className="inline-flex items-center -space-x-0.5">
      <LuBedSingle className="w-3.5 h-3.5" />
      <LuBedSingle className="w-3.5 h-3.5" />
    </span>
  );
}

// ─── Chip Toggle with Icon ───────────────────────────────────────────────────

function ChipToggleWithIcon({ label, active, onClick, icon }: { label: string; active: boolean; onClick: () => void; icon: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2.5 rounded-full border text-sm font-medium transition-all duration-300 inline-flex items-center gap-2 ${
        active
          ? 'border-[#801515] bg-[#801515] text-white shadow-md'
          : 'border-gray-200 bg-[#faf8f6] text-gray-700 hover:border-[#801515]/30'
      }`}
    >
      {icon}
      {active && <Check className="w-3.5 h-3.5 -mt-0.5" />}
      {label}
    </button>
  );
}

// ─── Corporate Inquiry Modal ─────────────────────────────────────────────────

function DatePickerField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const t = useTranslations('design.corporate');
  const locale = useLocale();

  const formatDate = (iso: string) => {
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString(localeDateTag(locale), { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      <div className="relative">
        <button
          type="button"
          onClick={() => ref.current?.showPicker()}
          className={`w-full px-4 py-3 rounded-xl border border-gray-200 bg-[#faf8f6] text-left transition-all duration-300 focus:border-[#801515] focus:ring-2 focus:ring-[#801515]/10 outline-none flex items-center justify-between ${
            value ? 'text-gray-900' : 'text-gray-400'
          }`}
        >
          <span>{value ? formatDate(value) : t('form.pickDate')}</span>
          <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
          </svg>
        </button>
        <input
          ref={ref}
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 opacity-0 pointer-events-none"
          tabIndex={-1}
        />
      </div>
    </div>
  );
}

function CorporateInquiryModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useTranslations('design.corporate');
  const locale = useLocale();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Step 1: Contact
  const [companyName, setCompanyName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  // Step 2: Dates
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [altDates, setAltDates] = useState('');

  // Step 3: Group
  const [participants, setParticipants] = useState('');
  const [rooms, setRooms] = useState('');
  const [bedPreference, setBedPreference] = useState<string[]>([]);

  // Step 4: Exclusivity
  const [exclusivity, setExclusivity] = useState('');

  // Step 5: Event type
  const [eventType, setEventType] = useState('');
  const [eventTypeOther, setEventTypeOther] = useState('');

  // Step 6: Food & Drinks
  const [meals, setMeals] = useState<string[]>([]);
  const [drinks, setDrinks] = useState<string[]>([]);
  const [openBarDuration, setOpenBarDuration] = useState('');
  const [coffeeBreak, setCoffeeBreak] = useState(false);
  const [foodOther, setFoodOther] = useState('');

  // Step 7: Spaces & Experiences
  const [meetingRoom, setMeetingRoom] = useState('');
  const [experiences, setExperiences] = useState<string[]>([]);

  // Step 8: Final details
  const [anythingElse, setAnythingElse] = useState('');

  // Step 9: Budget
  const [budget, setBudget] = useState('');

  // Newsletter opt-in → Corporate audience (doc 78 §Go-live)
  const [wantsNewsletter, setWantsNewsletter] = useState(false);

  const exclusivityOptions = [
    t('form.exclusivity.notNecessary'),
    t('form.exclusivity.preferred'),
    t('form.exclusivity.onlyWith'),
  ];

  const eventTypeOptions = [
    t('form.eventTypes.meeting'),
    t('form.eventTypes.training'),
    t('form.eventTypes.teamBuilding'),
    t('form.eventTypes.incentive'),
  ];

  const mealOptions = [
    t('form.meals.lunchServed'),
    t('form.meals.lunchBuffet'),
    t('form.meals.dinnerServed'),
    t('form.meals.dinnerBuffet'),
    t('form.meals.bbq'),
    t('form.meals.gourmet'),
  ];

  const drinkOptions = [
    t('form.drinks.softDrinks'),
    t('form.drinks.openBar'),
    t('form.drinks.alaCarte'),
  ];

  const openBarHours = ['2h', '3h', '4h', '5h'];

  const experienceOptions = [
    t('form.experiences.spa'),
    t('form.experiences.dj'),
    t('form.experiences.bonfire'),
    t('form.experiences.liveMusic'),
    t('form.experiences.palinka'),
    t('form.experiences.workshops'),
    t('form.experiences.hike'),
    t('form.experiences.teamBuildingPro'),
    t('form.experiences.partyLocation'),
  ];

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => setSubmitted(false), 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch('/api/inquiries/corporate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName, contactPerson, email, phone,
          checkIn, checkOut, altDates,
          participants, rooms, bedPreference,
          exclusivity,
          eventType, eventTypeOther,
          meals, drinks, openBarDuration, coffeeBreak, foodOther,
          meetingRoom, experiences,
          anythingElse, budget,
          wantsNewsletter,
          locale,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || 'send_failed');
      }
      setSubmitted(true);
    } catch (err) {
      log.error('design.corporate.inquiry.submit_failed', { err });
      setSubmitError(t('form.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleItem = (list: string[], setList: React.Dispatch<React.SetStateAction<string[]>>, item: string) => {
    setList(prev => prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]);
  };

  if (!open) return null;

  const inputCls = "w-full px-4 py-3 rounded-xl border border-gray-200 bg-[#faf8f6] focus:border-[#801515] focus:ring-2 focus:ring-[#801515]/10 outline-none transition-all duration-300 text-gray-900";
  const labelCls = "block text-sm font-medium text-gray-700 mb-1.5";
  const dividerCls = "h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-10";

  const SectionHeading = ({ num, title }: { num: number; title: string }) => (
    <h3 className="font-poppins text-xl font-semibold text-gray-900 mb-6 flex items-center gap-3">
      <div className="w-8 h-8 rounded-full bg-[#801515]/10 flex items-center justify-center flex-shrink-0">
        <span className="text-[#801515] text-sm font-bold">{num}</span>
      </div>
      {title}
    </h3>
  );

  const ChipToggle = ({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) => (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2.5 rounded-full border text-sm font-medium transition-all duration-300 ${
        active
          ? 'border-[#801515] bg-[#801515] text-white shadow-md'
          : 'border-gray-200 bg-[#faf8f6] text-gray-700 hover:border-[#801515]/30'
      }`}
    >
      {active && <Check className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />}
      {label}
    </button>
  );

  const RadioCard = ({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) => (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-3 rounded-xl border text-sm font-medium transition-all duration-300 text-left ${
        active
          ? 'border-[#801515] bg-[#801515]/5 text-[#801515] shadow-md'
          : 'border-gray-200 bg-[#faf8f6] text-gray-700 hover:border-[#801515]/30 hover:bg-[#801515]/[0.02]'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-[90] flex items-start justify-center overflow-y-auto bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl mx-4 my-8 md:my-16 bg-white rounded-3xl shadow-2xl overflow-hidden animate-[slideInUp_0.5s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/20 hover:bg-black/40 backdrop-blur-sm flex items-center justify-center text-white transition-colors duration-300"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Hero */}
        <div className="relative h-48 md:h-56 overflow-hidden">
          <Image
            src="/design-images/uploads/1771580188562-sc_28.jpg"
            alt={t('form.heroAlt')}
            fill
            className="object-cover"
            style={{ objectPosition: '50% 71%' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/60" />
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-white/90 text-sm font-medium tracking-widest uppercase" style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.5)' }}>
              {t('form.heroTitle')}
            </p>
          </div>
        </div>

        {submitted ? (
          /* ── Success state ── */
          <div className="px-8 md:px-16 py-16 text-center">
            <div className="w-20 h-20 rounded-full bg-[#4A6741]/10 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-[#4A6741]" />
            </div>
            <h2 className="font-poppins text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('form.thankYou')}
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-2">
              {t('form.requestReceived')}
            </p>
            <p className="text-gray-500 leading-relaxed mb-10">
              {t('form.replyMessage')}
            </p>
            <button
              onClick={onClose}
              className="inline-flex items-center gap-2 bg-[#801515] hover:bg-[#601010] text-white px-8 py-4 rounded-full font-medium transition-all duration-300 hover:shadow-xl"
            >
              {t('form.close')}
            </button>
          </div>
        ) : (
          /* ── Form ── */
          <form onSubmit={handleSubmit} className="px-8 md:px-16 py-10">
            <div className="text-center mb-10">
              <h2 className="font-poppins text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                {t('form.title')}
              </h2>
              <p className="text-gray-500 leading-relaxed max-w-lg mx-auto">
                {t('form.subtitle')}
              </p>
              <p className="text-sm text-gray-400 mt-2 italic">{t('form.timeNote')}</p>
            </div>

            {/* ── 1: Contact ── */}
            <div className="mb-10">
              <SectionHeading num={1} title={t('form.section1')} />
              <div className="mb-4">
                <label className={labelCls}>{t('form.companyName')} <span className="text-gray-400">({t('form.optional')})</span></label>
                <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} className={inputCls} placeholder={t('form.companyPlaceholder')} />
              </div>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelCls}>{t('form.contactPerson')}</label>
                  <input type="text" value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} className={inputCls} placeholder={t('form.contactPlaceholder')} />
                </div>
                <div>
                  <label className={labelCls}>{t('form.phone')}</label>
                  <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} placeholder="+40 ..." />
                </div>
              </div>
              <div>
                <label className={labelCls}>{t('form.email')} <span className="text-[#801515]">*</span></label>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="your@email.com" />
              </div>
            </div>

            <div className={dividerCls} />

            {/* ── 2: Dates ── */}
            <div className="mb-10">
              <SectionHeading num={2} title={t('form.section2')} />
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <DatePickerField label={t('form.checkIn')} value={checkIn} onChange={setCheckIn} />
                <DatePickerField label={t('form.checkOut')} value={checkOut} onChange={setCheckOut} />
              </div>
              <div>
                <label className={labelCls}>{t('form.altDates')} <span className="text-gray-400">({t('form.optional')})</span></label>
                <input type="text" value={altDates} onChange={(e) => setAltDates(e.target.value)} className={inputCls} placeholder={t('form.altDatesPlaceholder')} />
              </div>
            </div>

            <div className={dividerCls} />

            {/* ── 3: Group ── */}
            <div className="mb-10">
              <SectionHeading num={3} title={t('form.section3')} />
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelCls}>{t('form.participants')}</label>
                  <input type="text" value={participants} onChange={(e) => setParticipants(e.target.value)} className={inputCls} placeholder={t('form.participantsPlaceholder')} />
                </div>
                <div>
                  <label className={labelCls}>{t('form.rooms')} <span className="text-gray-400">({t('form.optional')})</span></label>
                  <input type="text" value={rooms} onChange={(e) => setRooms(e.target.value)} className={inputCls} placeholder={t('form.roomsPlaceholder')} />
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-3">{t('form.bedPreference')}</p>
                <div className="flex flex-wrap gap-3">
                  <ChipToggleWithIcon
                    label={t('form.bedSingle')}
                    active={bedPreference.includes('Single')}
                    onClick={() => toggleItem(bedPreference, setBedPreference, 'Single')}
                    icon={<LuBedSingle className="w-4 h-4" />}
                  />
                  <ChipToggleWithIcon
                    label={t('form.bedDouble')}
                    active={bedPreference.includes('Double')}
                    onClick={() => toggleItem(bedPreference, setBedPreference, 'Double')}
                    icon={<LuBedDouble className="w-4 h-4" />}
                  />
                  <ChipToggleWithIcon
                    label={t('form.bedTwin')}
                    active={bedPreference.includes('Twin')}
                    onClick={() => toggleItem(bedPreference, setBedPreference, 'Twin')}
                    icon={<TwinBedIcon />}
                  />
                </div>
              </div>
            </div>

            <div className={dividerCls} />

            {/* ── 4: Exclusivity ── */}
            <div className="mb-10">
              <SectionHeading num={4} title={t('form.section4')} />
              <p className="text-sm text-gray-500 mb-4 -mt-2">{t('form.exclusivityDesc')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {exclusivityOptions.map((opt) => (
                  <RadioCard key={opt} label={opt} active={exclusivity === opt} onClick={() => setExclusivity(opt)} />
                ))}
              </div>
            </div>

            <div className={dividerCls} />

            {/* ── 5: Event type ── */}
            <div className="mb-10">
              <SectionHeading num={5} title={t('form.section5')} />
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {eventTypeOptions.map((tp) => (
                  <RadioCard key={tp} label={tp} active={eventType === tp} onClick={() => setEventType(tp)} />
                ))}
                <RadioCard label={t('form.other')} active={eventType === 'Other'} onClick={() => setEventType('Other')} />
              </div>
              {eventType === 'Other' && (
                <input
                  type="text"
                  value={eventTypeOther}
                  onChange={(e) => setEventTypeOther(e.target.value)}
                  className={`mt-3 ${inputCls}`}
                  placeholder={t('form.otherEventPlaceholder')}
                />
              )}
            </div>

            <div className={dividerCls} />

            {/* ── 6: Food & Drinks ── */}
            <div className="mb-10">
              <SectionHeading num={6} title={t('form.section6')} />
              <p className="text-sm text-gray-500 mb-6 -mt-2">{t('form.foodDrinksDesc')}</p>

              {/* Meals */}
              <p className="text-sm font-semibold text-gray-800 mb-3">{t('form.mealsLabel')}</p>
              <div className="flex flex-wrap gap-3 mb-6">
                {mealOptions.map((item) => (
                  <ChipToggle key={item} label={item} active={meals.includes(item)} onClick={() => toggleItem(meals, setMeals, item)} />
                ))}
              </div>

              {/* Drinks */}
              <p className="text-sm font-semibold text-gray-800 mb-3">{t('form.drinksLabel')}</p>
              <div className="flex flex-wrap gap-3 mb-4">
                {drinkOptions.map((item) => (
                  <ChipToggle key={item} label={item} active={drinks.includes(item)} onClick={() => toggleItem(drinks, setDrinks, item)} />
                ))}
              </div>
              {drinks.includes(t('form.drinks.openBar')) && (
                <div className="ml-4 mb-6">
                  <p className="text-sm text-gray-600 mb-2">{t('form.openBarDuration')}:</p>
                  <div className="flex flex-wrap gap-2">
                    {openBarHours.map((h) => (
                      <button
                        key={h}
                        type="button"
                        onClick={() => setOpenBarDuration(h)}
                        className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all duration-300 ${
                          openBarDuration === h
                            ? 'border-[#801515] bg-[#801515]/5 text-[#801515]'
                            : 'border-gray-200 bg-[#faf8f6] text-gray-700 hover:border-[#801515]/30'
                        }`}
                      >
                        {h}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Breaks */}
              <p className="text-sm font-semibold text-gray-800 mb-3">{t('form.breaksLabel')}</p>
              <div className="flex flex-wrap gap-3 mb-6">
                <ChipToggle label={t('form.coffeeBreak')} active={coffeeBreak} onClick={() => setCoffeeBreak(!coffeeBreak)} />
              </div>

              {/* Other */}
              <div>
                <label className={labelCls}>{t('form.otherRequests')} <span className="text-gray-400">({t('form.optional')})</span></label>
                <input type="text" value={foodOther} onChange={(e) => setFoodOther(e.target.value)} className={inputCls} placeholder={t('form.dietaryPlaceholder')} />
              </div>
            </div>

            <div className={dividerCls} />

            {/* ── 7: Spaces & Experiences ── */}
            <div className="mb-10">
              <SectionHeading num={7} title={t('form.section7')} />
              <div className="mb-6">
                <label className={labelCls}>{t('form.meetingRoom')} <span className="text-gray-400">({t('form.optional')})</span></label>
                <input type="text" value={meetingRoom} onChange={(e) => setMeetingRoom(e.target.value)} className={inputCls} placeholder={t('form.meetingRoomPlaceholder')} />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800 mb-3">{t('form.experiences.label')}</p>
                <div className="flex flex-wrap gap-3">
                  {experienceOptions.map((item) => (
                    <ChipToggle key={item} label={item} active={experiences.includes(item)} onClick={() => toggleItem(experiences, setExperiences, item)} />
                  ))}
                </div>
              </div>
            </div>

            <div className={dividerCls} />

            {/* ── 8: Final details ── */}
            <div className="mb-10">
              <SectionHeading num={8} title={t('form.section8')} />
              <textarea
                value={anythingElse}
                onChange={(e) => setAnythingElse(e.target.value)}
                rows={4}
                className={`${inputCls} resize-none`}
                placeholder={t('form.anythingElsePlaceholder')}
              />
            </div>

            <div className={dividerCls} />

            {/* ── 9: Budget ── */}
            <div className="mb-10">
              <SectionHeading num={9} title={t('form.section9')} />
              <p className="text-sm text-gray-500 mb-4 -mt-2">{t('form.budgetDesc')}</p>
              <div>
                <label className={labelCls}>{t('form.budgetLabel')}</label>
                <input type="text" value={budget} onChange={(e) => setBudget(e.target.value)} className={inputCls} placeholder={t('form.budgetPlaceholder')} />
              </div>
            </div>

            {/* ── Newsletter opt-in ── */}
            <label className="flex items-start gap-3 mx-auto max-w-md mb-6 cursor-pointer text-left">
              <input
                type="checkbox"
                checked={wantsNewsletter}
                onChange={(e) => setWantsNewsletter(e.target.checked)}
                className="mt-1 h-5 w-5 rounded border-gray-300 text-[#801515] focus:ring-[#801515]/30"
              />
              <span className="text-sm text-gray-600">{t('form.newsletterOptIn')}</span>
            </label>

            {/* ── Submit ── */}
            <div className="text-center pt-2 pb-4">
              {submitError && (
                <div className="mb-4 mx-auto max-w-md px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                  {submitError}
                </div>
              )}
              <button
                type="submit"
                disabled={submitting || !email}
                className="inline-flex items-center gap-3 bg-[#801515] hover:bg-[#601010] disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-10 py-4 rounded-full font-medium text-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 group"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('form.sending')}
                  </>
                ) : (
                  <>
                    {t('form.submitButton')}
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function CorporatePage() {
  const t = useTranslations('design.corporate');

  const venues = [
    {
      name: t('venues.ballRoom.name'),
      size: t('venues.ballRoom.size'),
      tagline: t('venues.ballRoom.tagline'),
      description: t('venues.ballRoom.description'),
      tech: [t('venues.tech.projector'), t('venues.tech.screen'), t('venues.tech.sound'), t('venues.tech.flipChart'), t('venues.tech.wifi')],
      coffeeBreak: t('venues.ballRoom.coffeeBreak'),
      seating: [
        { type: t('venues.seatingTypes.uShape'), capacity: 18, icon: 'U' },
        { type: t('venues.seatingTypes.vShape'), capacity: 36, icon: 'V' },
        { type: t('venues.seatingTypes.theatre'), capacity: 48, icon: 'T' },
      ],
      image: '/design-images/corporate/ball-room-conference-u-shape.jpg',
    },
    {
      name: t('venues.barn.name'),
      size: t('venues.barn.size'),
      tagline: t('venues.barn.tagline'),
      description: t('venues.barn.description'),
      tech: [t('venues.tech.projector'), t('venues.tech.giantScreen'), t('venues.tech.proSound'), t('venues.tech.flipChart'), t('venues.tech.mobileWifi'), t('venues.tech.stage'), t('venues.tech.partyLights')],
      coffeeBreak: t('venues.barn.coffeeBreak'),
      seating: [
        { type: t('venues.seatingTypes.cabaret'), capacity: 32, icon: 'C' },
        { type: t('venues.seatingTypes.theatre'), capacity: 100, icon: 'T' },
      ],
      image: '/design-images/corporate/presentation-screening-barn.jpg',
    },
    {
      name: t('venues.exhibition.name'),
      size: t('venues.exhibition.size'),
      tagline: t('venues.exhibition.tagline'),
      description: t('venues.exhibition.description'),
      tech: [t('venues.tech.projector'), t('venues.tech.giantScreen'), t('venues.tech.sound'), t('venues.tech.flipChart'), t('venues.tech.mobileWifi')],
      coffeeBreak: t('venues.exhibition.coffeeBreak'),
      seating: [
        { type: t('venues.seatingTypes.theatre'), capacity: 20, icon: 'T' },
      ],
      image: '/design-images/corporate/medieval-fresco-exhibition-room.jpg',
    },
  ];

  const thematicDinners = [
    {
      name: t('dinners.gourmet.name'),
      subtitle: t('dinners.gourmet.subtitle'),
      description: t('dinners.gourmet.description'),
      image: '/design-images/corporate/restaurant-fine-dining.jpg',
    },
    {
      name: t('dinners.bonfire.name'),
      subtitle: t('dinners.bonfire.subtitle'),
      description: t('dinners.bonfire.description'),
      image: '/design-images/uploads/1777975065786-ana-georgian-1222-of-1232-_resized.jpg',
    },
    {
      name: t('dinners.cheese.name'),
      subtitle: t('dinners.cheese.subtitle'),
      description: t('dinners.cheese.description'),
      image: '/design-images/corporate/artisan-cheese-board.jpg',
    },
    {
      name: t('dinners.wine.name'),
      subtitle: t('dinners.wine.subtitle'),
      description: t('dinners.wine.description'),
      image: '/design-images/corporate/wine-selection-sommelier.jpg',
    },
  ];

  const activities = [
    { name: t('activities.spa.name'), duration: t('activities.spa.duration'), description: t('activities.spa.description'), category: 'wellness' },
    { name: t('activities.bonfire.name'), duration: t('activities.bonfire.duration'), description: t('activities.bonfire.description'), category: 'transformative' },
    { name: t('activities.hiking.name'), duration: t('activities.hiking.duration'), description: t('activities.hiking.description'), category: 'energising' },
    { name: t('activities.stretching.name'), duration: t('activities.stretching.duration'), description: t('activities.stretching.description'), category: 'energising' },
    { name: t('activities.gingerbread.name'), duration: t('activities.gingerbread.duration'), description: t('activities.gingerbread.description'), category: 'creative' },
    { name: t('activities.woodPainting.name'), duration: t('activities.woodPainting.duration'), description: t('activities.woodPainting.description'), category: 'creative' },
    { name: t('activities.perfume.name'), duration: t('activities.perfume.duration'), description: t('activities.perfume.description'), category: 'creative' },
    { name: t('activities.blacksmith.name'), duration: t('activities.blacksmith.duration'), description: t('activities.blacksmith.description'), category: 'creative' },
    { name: t('activities.teamBuilding.name'), duration: t('activities.teamBuilding.duration'), description: t('activities.teamBuilding.description'), category: 'energising' },
  ];

  const categoryLabels: Record<string, string> = {
    wellness: t('activities.categories.wellness'),
    transformative: t('activities.categories.transformative'),
    energising: t('activities.categories.energising'),
    creative: t('activities.categories.creative'),
  };

  useScrollReveal();

  const [inquiryOpen, setInquiryOpen] = useState(false);

  return (
    <main className="min-h-screen bg-white text-gray-900">
      <style jsx global>{`
        @keyframes breathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
        @keyframes breatheSlow {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        /* slideInUp kept — the inquiry modal animates in with it (see animate-[slideInUp_…]). */
        @keyframes slideInUp {
          from { opacity: 0; transform: translateY(60px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }
        .image-breathe {
          animation: breathe 8s ease-in-out infinite;
        }
        .image-breathe-slow {
          animation: breatheSlow 10s ease-in-out infinite;
        }
      `}</style>

      <NavPreview />

      {/* ━━━ HERO ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="relative h-screen w-full overflow-hidden">
        <div className="absolute inset-0">
          <Image
            src="/design-images/uploads/1771580188562-sc_28.jpg"
            alt={t('hero.imageAlt')}
            fill
            className="object-cover image-breathe-slow"
            style={{ objectPosition: '50% 71%' }}
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />

        <div className="relative h-full flex flex-col items-center justify-center px-6 pt-56 lg:pt-16 text-center">
          <div className="observe-me slide-up w-full max-w-3xl">
            <h1 className="font-poppins text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight" style={{ textShadow: '2px 2px 30px rgba(0,0,0,0.5)' }}>
              {t('hero.title')}
            </h1>
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="h-[2px] w-20 bg-[#801515]" style={{ animation: 'pulse 3s ease-in-out infinite' }} />
              <p className="text-base md:text-lg uppercase tracking-[0.3em] text-white/90 [text-shadow:rgba(0,0,0,1)_1px_1px_3px] md:[text-shadow:rgb(0,0,0)_1.5px_1.5px_4px]" style={{ backdropFilter: 'blur(1px)', borderRadius: '8px' }}>
                {t('hero.subtitle')}
              </p>
              <div className="h-[2px] w-20 bg-[#801515]" style={{ animation: 'pulse 3s ease-in-out infinite', animationDelay: '1.5s' }} />
            </div>
            <p className="text-3xl md:text-4xl text-white/95 w-fit max-w-3xl mx-auto font-light [text-shadow:rgba(0,0,0,1)_1.5px_1.5px_4px] md:[text-shadow:rgb(0,0,0)_2px_2px_6px]" style={{ backdropFilter: 'blur(1px)', borderRadius: '8px' }}>
              {t('hero.tagline')}
            </p>
          </div>
        </div>

      </section>

      {/* ━━━ INSPIRE — Why Choose Us ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="grid lg:grid-cols-5 min-h-screen design-section-gap rounded-3xl overflow-hidden">
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-[#faf8f6]">
          <div className="max-w-lg observe-me slide-left">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#801515]">{t('inspire.headingBold')}</span>
              <br />
              <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('inspire.headingLight')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-8">
              {t('inspire.description')}
            </p>

            {/* Key selling points */}
            <div className="space-y-4">
              {[
                { title: t('inspire.point1Title'), text: t('inspire.point1Text') },
                { title: t('inspire.point2Title'), text: t('inspire.point2Text') },
                { title: t('inspire.point3Title'), text: t('inspire.point3Text') },
                { title: t('inspire.point4Title'), text: t('inspire.point4Text') },
              ].map((point, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-2 h-2 bg-[#801515] rounded-full mt-3" />
                  <div>
                    <span className="font-semibold text-gray-900">{point.title}:</span>{' '}
                    <span className="text-gray-600">{point.text}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 observe-me slide-right">
          <Image
            src="/design-images/corporate/castle-wooden-balcony-golden-hour.jpg"
            alt={t('inspire.imageAlt')}
            fill
            className="object-cover image-breathe hover:scale-110 transition-transform duration-700"
            style={{ objectPosition: '50% 50%' }}
          />
        </div>
      </section>

      {/* ━━━ ACCOMMODATE ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="grid lg:grid-cols-5 min-h-[90vh] design-section-gap rounded-3xl overflow-hidden">
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 order-2 lg:order-1 observe-me slide-left">
          <Image
            src="/design-images/uploads/1771424029940-8_dc_constantinople_1.jpg"
            alt={t('stay.imageAlt')}
            fill
            className="object-cover image-breathe hover:scale-110 transition-transform duration-700"
          />
        </div>

        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-white order-1 lg:order-2">
          <div className="max-w-lg observe-me slide-right">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#801515]">{t('stay.headingBold')}</span>
              <br />
              <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('stay.headingLight')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-6">
              {t('stay.description')}
              <span className="italic">{t('stay.additionalRooms')}</span>
            </p>

            {/* Building breakdown */}
            <div className="grid grid-cols-3 gap-3 mb-8">
              <div className="bg-[#faf8f6] rounded-2xl p-4 text-center">
                <p className="text-3xl font-bold text-[#801515] mb-1">
                  8<span className="text-lg italic text-gray-400">+3</span>
                </p>
                <p className="text-sm text-gray-600 font-medium">{t('stay.danielCastle')}</p>
              </div>
              <div className="bg-[#faf8f6] rounded-2xl p-4 text-center">
                <p className="text-3xl font-bold text-[#801515] mb-1">12</p>
                <p className="text-sm text-gray-600 font-medium">{t('stay.bethlenCastle')}</p>
              </div>
              <div className="bg-[#faf8f6] rounded-2xl p-4 text-center">
                <p className="text-3xl font-bold text-gray-400 italic mb-1">7</p>
                <p className="text-sm text-gray-600 font-medium">{t('stay.craftsHouse')}</p>
              </div>
            </div>

            {/* Room categories */}
            <div className="space-y-3 mb-8 text-gray-600">
              {[
                { type: t('stay.standard'), main: 5, extra: 3 },
                { type: t('stay.superior'), main: 9, extra: 1 },
                { type: t('stay.deluxe'), main: 3, extra: 6 },
                { type: t('stay.signature'), main: 3, extra: undefined },
              ].map((r) => (
                <div key={r.type} className="flex items-center justify-between text-gray-900 font-medium">
                  <span>{r.type}</span>
                  {r.extra !== undefined ? (
                    <span className="font-semibold text-[#801515]">
                      {r.main}<span className="italic text-gray-400 text-sm">+{r.extra}</span> {t('stay.roomsUnit')}
                    </span>
                  ) : (
                    <span className="font-semibold text-[#801515]">{r.main} {t('stay.roomsUnit')}</span>
                  )}
                </div>
              ))}
            </div>

            {/* Capacity badges */}
            <div className="flex flex-wrap gap-3">
              <div className="bg-[#801515] text-white px-5 py-2 rounded-full text-sm font-medium">
                {t('stay.capacityStandardLabel')}: 40<span className="ml-1 italic text-gray-200 text-xs">+ 10</span> {t('stay.capacitySuffix')}
              </div>
              <div className="bg-[#801515]/80 text-white px-5 py-2 rounded-full text-sm font-medium">
                {t('stay.capacityMaximumLabel')}: 50<span className="ml-1 italic text-gray-200 text-xs">+ 10</span> {t('stay.capacitySuffix')}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━ Full-width image break — Bethlen Castle ━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="relative h-[50vh] overflow-hidden bg-gray-100 observe-me fade-in design-section-gap rounded-3xl">
        <Image
          src="/design-images/corporate/castle-manor-house-autumn.jpg"
          alt={t('stay.bethlenImageAlt')}
          fill
          className="object-cover image-breathe-slow"
          style={{ objectPosition: '50% 19%' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-transparent to-white/40" />
      </section>

      {/* ━━━ CONVENE — Meeting Venues ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="grid lg:grid-cols-5 min-h-screen design-section-gap rounded-3xl overflow-hidden">
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-gradient-to-br from-[#f5f3f0] to-[#faf8f6]">
          <div className="max-w-lg observe-me slide-left">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#801515]">{t('convene.headingBold')}</span>
              <br />
              <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('convene.headingLight')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-5">
              {t('convene.p1')}
            </p>
            <p className="text-lg xl:text-xl text-gray-700 leading-relaxed font-medium">
              {t('convene.p2')}
            </p>
          </div>
        </div>

        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 observe-me slide-right">
          <Image
            src="/design-images/uploads/1774002657104-sc_26.jpg"
            alt={t('convene.imageAlt')}
            fill
            className="object-cover image-breathe hover:scale-110 transition-transform duration-700"
          />
        </div>
      </section>

      {/* Venue Cards */}
      <section className="design-section-gap">
        <div className="grid md:grid-cols-3 gap-6">
          {venues.map((venue, index) => (
            <VenueCard key={venue.name} venue={venue} index={index} />
          ))}
        </div>
      </section>

      {/* ━━━ UNWIND — Social Spaces ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="design-section-gap">
        <div className="text-center mb-10 pt-16 observe-me slide-up">
          <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-4">
            <span className="text-[#801515]">{t('unwind.heading')}</span>
          </h2>
          <p className="text-gray-800 text-2xl md:text-3xl font-normal">
            {t('unwind.subheading')}
          </p>
          <div className="w-16 h-1 bg-[#801515] mx-auto mt-6 mb-4" />
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t('unwind.description')}
          </p>
        </div>

        {/* Social spaces image grid */}
        <div className="grid md:grid-cols-3 gap-4 rounded-3xl overflow-hidden">
          {[
            { image: '/design-images/corporate/bethlen-castle-lounge.jpg', title: t('unwind.lounge'), subtitle: t('unwind.loungeLocation') },
            { image: '/design-images/corporate/ball-room-fireplace-lounge.jpg', title: t('unwind.fireside'), subtitle: t('unwind.firesideLocation') },
            { image: '/design-images/corporate/team-gathering-bean-bags-barn.jpg', title: t('unwind.chill'), subtitle: t('unwind.chillLocation') },
          ].map((space, i) => (
            <div key={i} className="relative h-80 overflow-hidden group observe-me slide-up" style={{ animationDelay: `${i * 0.15}s` }}>
              <Image
                src={space.image}
                alt={space.title}
                fill
                className="object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-5 right-5">
                <h3 className="text-xl font-bold text-white">{space.title}</h3>
                <p className="text-white/70 text-sm">{space.subtitle}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ━━━ Coffee break — full width ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="grid lg:grid-cols-5 min-h-[70vh] design-section-gap rounded-3xl overflow-hidden">
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 order-2 lg:order-1 observe-me slide-left">
          <Image
            src="/design-images/corporate/coffee-break-library-corner.jpg"
            alt={t('pause.imageAlt')}
            fill
            className="object-cover image-breathe hover:scale-110 transition-transform duration-700"
          />
        </div>

        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-gradient-to-br from-[#f9f5f2] to-white order-1 lg:order-2">
          <div className="max-w-lg observe-me slide-right">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#801515]">{t('pause.headingBold')}</span>
              <br />
              <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('pause.headingLight')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-6">
              {t('pause.intro')}
            </p>
            <div className="space-y-4">
              {[
                { label: t('pause.beveragesLabel'), text: t('pause.beveragesText') },
                { label: t('pause.snacksLabel'), text: t('pause.snacksText') },
                { label: t('pause.hydrationLabel'), text: t('pause.hydrationText') },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-2 h-2 bg-[#801515] rounded-full mt-3" />
                  <p className="text-gray-600">
                    <strong className="text-gray-900">{item.label}{item.text ? ':' : ''}</strong> {item.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ━━━ SAVOUR — Dining ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="grid lg:grid-cols-5 min-h-screen design-section-gap rounded-3xl overflow-hidden">
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-[#faf8f6]">
          <div className="max-w-lg observe-me slide-left">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#801515]">{t('savour.headingBold')}</span>
              <br />
              <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('savour.headingLight')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-8">
              {t('savour.description')}
            </p>

            {/* Dining options */}
            <div className="space-y-4 mb-8">
              {[
                { type: t('savour.setMenu'), desc: t('savour.setMenuDesc') },
                { type: t('savour.alaCarte'), desc: t('savour.alaCarteDesc') },
                { type: t('savour.buffet'), desc: t('savour.buffetDesc') },
              ].map((opt, i) => (
                <div key={i} className="bg-white rounded-xl px-5 py-4 shadow-sm">
                  <p className="font-semibold text-[#801515]">{opt.type}</p>
                  <p className="text-sm text-gray-600">{opt.desc}</p>
                </div>
              ))}
            </div>

            {/* Grill terrace mention */}
            <p className="text-gray-600 italic">
              {t('savour.grillTerrace')}
            </p>
          </div>
        </div>

        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 observe-me slide-right">
          <Image
            src="/design-images/corporate/restaurant-fine-dining.jpg"
            alt={t('savour.imageAlt')}
            fill
            className="object-cover image-breathe hover:scale-110 transition-transform duration-700"
          />
        </div>
      </section>

      {/* ━━━ Thematic Dinners ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="design-section-gap">
        <div className="text-center mb-10 pt-16 observe-me slide-up">
          <h2 className="font-poppins text-4xl md:text-5xl xl:text-6xl font-bold mb-4">
            <span className="text-[#801515]">{t('dinners.heading')}</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            {t('dinners.subheading')}
          </p>
          <div className="w-16 h-1 bg-[#801515] mx-auto mt-6" />
        </div>

        {/* Dinner cards — 2x2 balanced grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {thematicDinners.map((dinner, i) => (
            <div
              key={i}
              className="observe-me slide-up group relative rounded-3xl overflow-hidden h-96 shadow-lg hover:shadow-2xl transition-all duration-500"
              style={{ animationDelay: `${i * 0.15}s` }}
            >
              <Image
                src={dinner.image}
                alt={dinner.name}
                fill
                className="object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-xl font-bold text-white mb-1">{dinner.name}</h3>
                <p className="text-white/70 text-sm italic mb-2">{dinner.subtitle}</p>
                <p className="text-white/80 text-sm leading-relaxed">{dinner.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ━━━ Full-width image break — Group dinner ━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="relative h-[50vh] overflow-hidden bg-gray-100 observe-me fade-in design-section-gap rounded-3xl">
        <Image
          src="/design-images/corporate/group-dinner-wine-toast.jpg"
          alt={t('dinners.groupImageAlt')}
          fill
          className="object-cover image-breathe-slow"
          style={{ objectPosition: '50% 33%' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-white/30" />
      </section>

      {/* ━━━ EXPERIENCE — Activities ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="grid lg:grid-cols-5 min-h-screen design-section-gap rounded-3xl overflow-hidden">
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 order-2 lg:order-1 observe-me slide-left">
          <Image
            src="/design-images/corporate/hiking-gorge-overlook.jpg"
            alt={t('experience.imageAlt')}
            fill
            className="object-cover image-breathe hover:scale-110 transition-transform duration-700"
          />
        </div>

        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-gradient-to-br from-[#f0f4f0] to-white order-1 lg:order-2">
          <div className="max-w-lg observe-me slide-right">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#4A6741]">{t('experience.headingBold')}</span>
              <br />
              <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('experience.headingLight')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#4A6741] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-5">
              {t('experience.p1')}
            </p>
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed">
              {t('experience.p2')}
            </p>
          </div>
        </div>
      </section>

      {/* Activity cards */}
      <section className="design-section-gap">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {activities.map((activity, i) => {
            const categoryColors: Record<string, string> = {
              wellness: '#478B8B',
              transformative: '#801515',
              energising: '#4A6741',
              creative: '#8B6914'
            };
            const color = categoryColors[activity.category] || '#801515';

            return (
              <div
                key={i}
                className="observe-me slide-up bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border-l-4"
                style={{ borderLeftColor: color, animationDelay: `${i * 0.1}s` }}
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-bold text-gray-900 font-poppins leading-tight">{activity.name}</h3>
                  {activity.duration && (
                    <span
                      className="text-xs font-bold px-3 py-1 rounded-2xl text-white ml-3 text-center break-words max-w-[45%]"
                      style={{ backgroundColor: color }}
                    >
                      {activity.duration}
                    </span>
                  )}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{activity.description}</p>
                <p className="text-xs font-medium uppercase tracking-wider mt-3" style={{ color }}>
                  {categoryLabels[activity.category] || activity.category}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ━━━ Activity image break — Blacksmith ━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="grid lg:grid-cols-2 h-[50vh] design-section-gap rounded-3xl overflow-hidden observe-me fade-in">
        <div className="relative h-full overflow-hidden">
          <Image
            src="/design-images/corporate/blacksmith-forge.jpg"
            alt={t('experience.blacksmithAlt')}
            fill
            className="object-cover hover:scale-105 transition-transform duration-700"
          />
        </div>
        <div className="relative h-full overflow-hidden">
          <Image
            src="/design-images/corporate/palinca-tasting-fire-pit.jpg"
            alt={t('experience.palinkaAlt')}
            fill
            className="object-cover hover:scale-105 transition-transform duration-700"
          />
        </div>
      </section>

      {/* ━━━ TESTIMONIALS ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-b from-white via-[#faf8f6] to-white overflow-hidden mt-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 observe-me slide-up">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6">
              <span className="text-[#801515]">{t('trusted.heading')}</span>
              <br />
              <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('trusted.subheading')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mx-auto" />
          </div>

          <TestimonialsCarousel />
        </div>

        {/* Decorative elements */}
        <div className="absolute top-32 right-10 w-2 h-2 bg-[#801515] rounded-full opacity-40" style={{ animation: 'pulse 4s ease-in-out infinite' }} />
        <div className="absolute bottom-20 left-16 w-3 h-3 bg-[#4A6741] rounded-full opacity-35" style={{ animation: 'pulse 5s ease-in-out infinite', animationDelay: '1s' }} />
      </section>

      {/* ━━━ FAQ ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-b from-white to-[#faf8f6]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16 observe-me slide-up">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6">
              <span className="text-[#801515]">{t('faqSection.heading')}</span>
              <br />
              <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('faqSection.subheading')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mx-auto" />
          </div>

          <FAQAccordion />
        </div>
      </section>

      {/* ━━━ ENQUIRE — CTA ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="relative min-h-[80vh] flex items-center justify-center py-32 px-6 bg-gradient-to-b from-[#faf8f6] to-white">
        <div className="max-w-4xl mx-auto text-center observe-me slide-up">
          <h2 className="font-poppins text-4xl sm:text-6xl md:text-7xl xl:text-8xl font-bold mb-6 leading-[1.1]">
            <span className="text-[#801515]">{t('cta.heading')}</span>
          </h2>
          <p className="text-2xl md:text-3xl text-gray-700 leading-relaxed mb-6">
            {t('cta.question')}
          </p>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-12">
            {t('cta.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => setInquiryOpen(true)} className="group px-[26px] py-6 bg-[#801515] hover:bg-[#601010] text-white font-serif text-xl rounded-full transition-all duration-500 inline-flex items-center justify-center gap-3 shadow-xl hover:shadow-2xl hover:shadow-[#801515]/40 transform hover:scale-105">
              {t('cta.button')}
              <ArrowRight className="w-7 h-7 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 left-20 w-32 h-32 bg-[#801515]/5 rounded-full blur-3xl" style={{ animation: 'breatheSlow 8s ease-in-out infinite' }} />
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-[#4A6741]/5 rounded-full blur-3xl" style={{ animation: 'breatheSlow 10s ease-in-out infinite', animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-1/4 w-3 h-3 bg-[#801515] rounded-full opacity-40" style={{ animation: 'pulse 5s ease-in-out infinite' }} />
        <div className="absolute top-1/3 right-1/4 w-2 h-2 bg-[#4A6741] rounded-full opacity-35" style={{ animation: 'pulse 6s ease-in-out infinite', animationDelay: '1s' }} />
      </section>

      <Footer />

      {/* Floating Enquiry Button */}
      <button
        onClick={() => setInquiryOpen(true)}
        className="fixed right-8 z-50 inline-flex items-center gap-3 bg-[#801515] hover:bg-[#601010] text-white px-8 py-4 rounded-full font-medium shadow-2xl transition-all duration-300 hover:shadow-[0_8px_30px_rgba(128,21,21,0.4)] hover:-translate-y-0.5 group"
        style={{ bottom: 'calc(2rem + var(--selection-bar-height))' }}
      >
        {t('cta.button')}
        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
      </button>

      <CorporateInquiryModal open={inquiryOpen} onClose={() => setInquiryOpen(false)} />
    </main>
  );
}
